import { Companion, CompanionServiceRate } from '../types';
import { ALL_COMPANION_SERVICES, ServiceDefinition } from '../data/servicesData';

export interface ServiceItemWithCount extends ServiceDefinition {
  availableCompanionsCount: number;
  startingPrice: number;
}

// Normalized mapping helper
export const normalizeServiceName = (name: string): string => {
  return name.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
};

// Deterministic matching for companions without custom services list
const COMPANION_SPECIALTIES: Record<string, string[]> = {
  // West Bengal
  'riya-sharma': ['Movies', 'Shopping', 'Travel', 'Outdoor Events', 'Baking/Cooking', 'Hanging Out', 'Dinner', 'Music', 'Culture', 'Parties'],
  'priya-mukherjee-bardhaman': ['Culture', 'Music', 'Dinner', 'Hanging Out', 'Baking/Cooking', 'Shopping', 'Elder Care', 'Tutoring', 'Movies', 'Outdoor Events'],
  'abhik-roy-bardhaman': ['Playing Sports', 'Sporting Events', 'Tutoring', 'Business Events', 'Dinner', 'Hanging Out', 'Going to Bar', 'Music', 'Travel', 'Movies'],
  'sneha-sen-durgapur': ['Business Events', 'Tutoring', 'Playing Sports', 'Movies', 'Shopping', 'Dinner', 'Hanging Out', 'Comedy Club', 'Music', 'Culture'],
  'anirban-ghosh-durgapur': ['Sporting Events', 'Playing Sports', 'Music', 'Travel', 'Outdoor Events', 'Going to Bar', 'Parties', 'Dinner', 'Hanging Out', 'Comedy Club'],
  'payel-banerjee-siliguri': ['Travel', 'Outdoor Events', 'Culture', 'Shopping', 'Dinner', 'Hanging Out', 'Movies', 'Elder Care', 'Baking/Cooking'],
  'rohit-das-siliguri': ['Travel', 'Playing Sports', 'Sporting Events', 'Outdoor Events', 'Dinner', 'Going to Bar', 'Parties', 'Music', 'Hanging Out'],
  'tania-saha-asansol': ['Tutoring', 'Domestic Help', 'Medical Help', 'Elder Care', 'Baking/Cooking', 'Dinner', 'Hanging Out', 'Culture', 'Movies'],
  'sourav-chatterjee-howrah': ['Business Events', 'Going to Bar', 'Clubbing', 'Parties', 'Dinner', 'Movies', 'Comedy Club', 'Sporting Events', 'Hanging Out'],
  'debolina-sen-kolkata': ['Shopping', 'Travel', 'Clubbing', 'Parties', 'Dinner', 'Going to Bar', 'Music', 'Movies', 'Culture', 'Outdoor Events'],
  'arijit-mukherjee-kolkata': ['Music', 'Comedy Club', 'Movies', 'Dinner', 'Hanging Out', 'Going to Bar', 'Parties', 'Culture', 'Tutoring'],
  'shreya-bose-kolkata': ['Medical Help', 'Elder Care', 'Domestic Help', 'Tutoring', 'Baking/Cooking', 'Dinner', 'Hanging Out', 'Shopping', 'Movies'],
};

// Check if a companion offers a given service
export const isCompanionAvailableForService = (
  companion: Companion,
  serviceQuery: string
): boolean => {
  if (!serviceQuery || serviceQuery === 'all' || serviceQuery === 'All Occasions') {
    return true;
  }

  const queryNorm = normalizeServiceName(serviceQuery);

  // CRITICAL DRIVER RULE:
  // If service category is Driver, Companion MUST have Driving Licence approved by Admin.
  // Driver service remains inactive until verified & approved by Admin.
  if (queryNorm === 'driver') {
    const isLicenceApproved =
      companion.drivingLicenceStatus === 'APPROVED' || companion.isDriverApproved === true;
    if (!isLicenceApproved) {
      return false;
    }
  }

  // 1. If companion has explicitly defined services array (e.g. from profile edit)
  if (companion.services && companion.services.length > 0) {
    const found = companion.services.find(
      (s) =>
        normalizeServiceName(s.name) === queryNorm ||
        normalizeServiceName(s.id) === queryNorm
    );
    if (found) return found.isAvailable;
  }

  // 2. Check explicit specialty mapping
  const specialties = COMPANION_SPECIALTIES[companion.id];
  if (specialties && specialties.length > 0) {
    const hasSpecialty = specialties.some(
      (s) => normalizeServiceName(s) === queryNorm
    );
    if (hasSpecialty) return true;
  }

  // 3. Check supportedOccasions and interests
  const hasInOccasions = companion.supportedOccasions?.some(
    (occ) => normalizeServiceName(occ).includes(queryNorm) || queryNorm.includes(normalizeServiceName(occ))
  );
  if (hasInOccasions) return true;

  const hasInInterests = companion.interests?.some(
    (int) => normalizeServiceName(int).includes(queryNorm) || queryNorm.includes(normalizeServiceName(int))
  );
  if (hasInInterests) return true;

  // 4. Stable deterministic assignment for all other companions so that every service has ample companions
  // Hash companion.id with serviceQuery to make deterministic distribution
  let hash = 0;
  const combined = `${companion.id}_${queryNorm}`;
  for (let i = 0; i < combined.length; i++) {
    hash = (hash << 5) - hash + combined.charCodeAt(i);
    hash |= 0;
  }
  const positiveHash = Math.abs(hash);

  // Very common services: 75% of companions offer them
  const commonServices = ['movies', 'shopping', 'dinner', 'hangingout', 'outdoorevents', 'culture', 'parties', 'travel', 'touristguide'];
  if (commonServices.includes(queryNorm)) {
    return positiveHash % 100 < 75;
  }

  // Driver service (only for companions with driving licence approval): 50% distribution among verified drivers
  if (queryNorm === 'driver') {
    return positiveHash % 100 < 60;
  }

  // Specialized services: 50% of companions offer them
  return positiveHash % 100 < 50;
};

// Get the specific hourly rate of a companion for a given service
export const getCompanionRateForService = (
  companion: Companion,
  serviceQuery: string
): number => {
  const queryNorm = normalizeServiceName(serviceQuery);

  if (companion.services && companion.services.length > 0) {
    const found = companion.services.find(
      (s) =>
        normalizeServiceName(s.name) === queryNorm ||
        normalizeServiceName(s.id) === queryNorm
    );
    if (found && found.hourlyRate) {
      return found.hourlyRate;
    }
  }

  // Fallback to service default rate or companion hourly rate
  const serviceDef = ALL_COMPANION_SERVICES.find(
    (s) => normalizeServiceName(s.name) === queryNorm || normalizeServiceName(s.id) === queryNorm
  );

  if (serviceDef) {
    // If companion base rate is higher than default, use companion rate; otherwise blend
    return Math.max(companion.hourlyRate, serviceDef.defaultRate);
  }

  return companion.hourlyRate;
};

// Get service metadata with available companion counts
export const getServicesWithCompanionCounts = (
  companions: Companion[]
): ServiceItemWithCount[] => {
  return ALL_COMPANION_SERVICES.map((srv) => {
    const availableCompanions = companions.filter((comp) =>
      isCompanionAvailableForService(comp, srv.name)
    );

    const rates = availableCompanions.map((comp) =>
      getCompanionRateForService(comp, srv.name)
    );
    const minRate = rates.length > 0 ? Math.min(...rates) : srv.defaultRate;

    return {
      ...srv,
      availableCompanionsCount: availableCompanions.length,
      startingPrice: minRate
    };
  });
};
